import {
  selectHasSeenSurplusLinesModal,
  selectIsBookConversion,
  selectIsManualUWApproved,
  selectPolicyErrorState,
  selectPolicyLoading,
  setHasSeenSurplusLinesModal,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { isTestEnv } from "@covertree/utils";
import { useResetFlowOnTabReopen, useResetNewPolicy } from "@hooks/newPolicy";
import { useAppDispatch } from "@redux/hooks";
import {
  BASE_NEW_POLICY_PATH,
  getNewPolicyPath,
  NewPolicyRoutes,
  OVERVIEW_PATH,
  SEQUOIA_PATH,
} from "@routing/paths";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

const hideBackButtonOn = [
  NewPolicyRoutes.SELECT_PRODUCT,
  NewPolicyRoutes.FAST_DECLINE,
  NewPolicyRoutes.CUSTOMIZE_POLICY,
  NewPolicyRoutes.CONGRATULATIONS,
];

const blockedToGoBackPaths = [NewPolicyRoutes.DECLINE_PROPERTY];

export const useNewPolicyLayout = () => {
  const { pathname, state: locationState } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resetNewPolicy = useResetNewPolicy();
  const policyErrorState = useNewBusinessSelector(selectPolicyErrorState);

  const [span, setSpan] = useState<string>("span 10");
  const showSurplusLinesFromCYQ = !!(locationState as Record<string, unknown>)
    ?.showSurplusLinesFromCYQ;
  const hasSeenSurplusLinesModal = useNewBusinessSelector(
    selectHasSeenSurplusLinesModal
  );
  const [isSurplusLinesModalOpen, setIsSurplusLinesModalOpen] = useState(false);

  useEffect(() => {
    if (showSurplusLinesFromCYQ && !hasSeenSurplusLinesModal) {
      setIsSurplusLinesModalOpen(true);
      dispatch(setHasSeenSurplusLinesModal(true));
    }
  }, [showSurplusLinesFromCYQ, hasSeenSurplusLinesModal, dispatch]);

  const isManualUWApproved = useNewBusinessSelector(selectIsManualUWApproved);

  const { show: showLoadingFrame, ...rest } =
    useNewBusinessSelector(selectPolicyLoading);

  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);

  useResetFlowOnTabReopen();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (true) {
        case e.ctrlKey && e.key === "Backspace":
          // TODO: add goBack();
          break;
        case e.ctrlKey && e.key === "Escape":
          navigate(`/${OVERVIEW_PATH}`);
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  const scrollToTop = useCallback(() => {
    document.querySelector(".MuiDrawer-paperAnchorBottom")?.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [pathname]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // TODO: Add areQuotesDocumentsLoading
  const showBackButton =
    (![...hideBackButtonOn, ...(isTestEnv ? [] : blockedToGoBackPaths)].some(
      (path) => pathname.includes(path)
    ) &&
      !isManualUWApproved) ||
    false;

  const showResetButton = useMemo(() => {
    if (
      isBookConversion ||
      pathname.includes(NewPolicyRoutes.CONGRATULATIONS)
    ) {
      return false;
    }
    return true;
  }, [isBookConversion, pathname]);

  const title = useMemo(() => {
    if (isBookConversion) {
      return "Sequoia Quote";
    }
    return "New Quote";
  }, [isBookConversion]);

  const resetFields = useCallback(() => {
    resetNewPolicy(`/${BASE_NEW_POLICY_PATH}`, true);
  }, [resetNewPolicy]);

  const handleClose = useCallback(() => {
    if (isBookConversion) {
      navigate(`/${SEQUOIA_PATH}`);
    } else {
      navigate(`/${OVERVIEW_PATH}`);
    }
  }, [isBookConversion, navigate]);

  useEffect(() => {
    switch (pathname) {
      case getNewPolicyPath(NewPolicyRoutes.HOME_ADDRESS):
      case getNewPolicyPath(NewPolicyRoutes.HOME_DETAILS):
      case getNewPolicyPath(NewPolicyRoutes.PERSONAL_DETAILS):
        setSpan("span 8");
        break;
      case getNewPolicyPath(NewPolicyRoutes.PERSONALIZED_QUOTES):
      case getNewPolicyPath(NewPolicyRoutes.EXTRA_COVERAGES):
      case getNewPolicyPath(NewPolicyRoutes.CLAIMS_LIST):
        setSpan("span 10");
        break;
      default:
        setSpan("span 9");
        break;
    }
  }, [pathname]);

  useEffect(() => {
    return () => {
      resetNewPolicy(undefined, true);
    };
  }, [resetNewPolicy]);

  return {
    title,
    showResetButton,
    resetFields,
    showBackButton,
    policyErrorState,
    handleClose,
    span,
    showLoadingFrame,
    rest,
    isSurplusLinesModalOpen,
    setIsSurplusLinesModalOpen,
  };
};
